import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Phone,
  PhoneOff,
  Video,
  StopCircle,
  Play,
  Pause,
  Download,
  Save,
  Bot,
  User,
  Loader2,
  Settings,
  FileText,
  Clock,
  Calendar,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Composant principal Assistant Vocal
const AssistantVocalPage = () => {
  // États
  const [mode, setMode] = useState('conversation'); // 'conversation' ou 'reunion'
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true); // L'IA répond en parlant
  const [transcript, setTranscript] = useState([]); // Historique des échanges
  const [currentText, setCurrentText] = useState(''); // Texte en cours de transcription
  const [selectedAgent, setSelectedAgent] = useState('direction'); // Agent principal (délègue aux autres)
  const [agents, setAgents] = useState([]);
  const [reunionTitle, setReunionTitle] = useState('');
  const [reunionDuration, setReunionDuration] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState('nova'); // Voix féminine agréable
  const [currentReunionId, setCurrentReunionId] = useState(null); // ID de la réunion en cours
  const [savingReunion, setSavingReunion] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const wsRef = useRef(null); // WebSocket pour streaming

  // Charger les agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/brain/agents`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setAgents(data.agents || []);
      } catch (error) {
        console.error('Erreur chargement agents:', error);
      }
    };
    fetchAgents();
  }, []);

  // Timer pour la durée de réunion
  useEffect(() => {
    if (mode === 'reunion' && isListening && !isPaused) {
      timerRef.current = setInterval(() => {
        setReunionDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [mode, isListening, isPaused]);

  // Formater la durée
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Démarrer l'écoute - CRÉER UN NOUVEAU SUJET pour cette session
  const startListening = async () => {
    try {
      // Créer un NOUVEAU sujet/session à chaque "décrochage"
      // Cela évite de mélanger les conversations
      const newSessionId = `vocal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('vocal_session_id', newSessionId);
      console.log('Nouvelle session vocale:', newSessionId);
      
      // Effacer le transcript local
      setTranscript([]);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      streamRef.current = stream;
      
      // Créer le MediaRecorder
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          await processAudio(blob);
          chunksRef.current = [];
        }
      };
      
      // Mode conversation : enregistrement par segments
      if (mode === 'conversation') {
        startSegmentRecording();
      } else {
        // Mode réunion : enregistrement continu avec segments de 30s
        startContinuousRecording();
      }
      
      setIsListening(true);
      setIsPaused(false);
      toast.success(mode === 'conversation' ? '🎙️ Je vous écoute...' : '📝 Enregistrement de réunion démarré');
      
    } catch (error) {
      console.error('Erreur micro:', error);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  // Enregistrement par segments (conversation)
  const startSegmentRecording = () => {
    if (!mediaRecorderRef.current) return;
    
    mediaRecorderRef.current.start();
    
    // Détecter le silence pour couper automatiquement
    silenceTimerRef.current = setTimeout(() => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
        // Redémarrer après traitement
        setTimeout(() => {
          if (isListening && !isPaused && streamRef.current) {
            chunksRef.current = [];
            mediaRecorderRef.current.start();
          }
        }, 500);
      }
    }, 5000); // 5 secondes de segment max
  };

  // Enregistrement continu (réunion)
  const startContinuousRecording = () => {
    if (!mediaRecorderRef.current) return;
    
    mediaRecorderRef.current.start();
    
    // Segments de 30 secondes pour transcription continue
    const segmentInterval = setInterval(() => {
      if (mediaRecorderRef.current?.state === 'recording' && isListening && !isPaused) {
        mediaRecorderRef.current.stop();
        setTimeout(() => {
          if (isListening && !isPaused && streamRef.current) {
            chunksRef.current = [];
            try {
              mediaRecorderRef.current.start();
            } catch (e) {
              console.log('Recorder already started');
            }
          }
        }, 100);
      }
    }, 30000);
    
    // Stocker l'interval pour le nettoyer
    timerRef.current = segmentInterval;
  };

  // Arrêter l'écoute
  const stopListening = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setIsListening(false);
    setIsPaused(false);
    toast.info('🛑 Écoute arrêtée');
  };

  // Pause/Reprise
  const togglePause = () => {
    if (isPaused) {
      // Reprendre
      if (mode === 'conversation') {
        startSegmentRecording();
      } else {
        startContinuousRecording();
      }
      setIsPaused(false);
      toast.info('▶️ Reprise');
    } else {
      // Pause
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      setIsPaused(true);
      toast.info('⏸️ Pause');
    }
  };

  // Traiter l'audio (transcription)
  const processAudio = async (blob) => {
    if (blob.size < 1000) return; // Ignorer les segments trop courts
    
    setCurrentText('Transcription...');
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      
      const response = await fetch(`${API_URL}/api/integrations/transcribe/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success && data.text && data.text.trim()) {
        const userText = data.text.trim();
        setCurrentText('');
        
        // Ajouter au transcript
        const userEntry = {
          role: 'user',
          content: userText,
          timestamp: new Date().toISOString()
        };
        setTranscript(prev => [...prev, userEntry]);
        
        // En mode conversation, obtenir une réponse de l'IA
        if (mode === 'conversation') {
          await getAIResponse(userText);
        }
      } else {
        setCurrentText('');
      }
    } catch (error) {
      console.error('Erreur transcription:', error);
      setCurrentText('');
    }
  };

  // Obtenir réponse IA avec session persistante
  const getAIResponse = async (userText) => {
    try {
      const token = localStorage.getItem('token');
      
      // Générer ou récupérer un ID de session vocal persistant
      let vocalSessionId = localStorage.getItem('vocal_session_id');
      if (!vocalSessionId) {
        vocalSessionId = `vocal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('vocal_session_id', vocalSessionId);
      }
      
      const response = await api.post('/brain/chat', {
        message: userText,
        agent_type: 'direction',
        conversation_id: vocalSessionId
      });
      
      const data = response.data;

      if (data.generated_document?.type === 'workspace_switch' && data.generated_document.societe_id) {
        localStorage.setItem('activeSocieteId', data.generated_document.societe_id);
        sessionStorage.setItem('activeSocieteIdOnglet', data.generated_document.societe_id);
        setTimeout(() => window.location.reload(), 2500);
      }
      
      if (data.response) {
        const aiEntry = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
          agent: 'general'
        };
        setTranscript(prev => [...prev, aiEntry]);
        
        // Lire la réponse à voix haute si activé
        if (autoSpeak) {
          await speakText(data.response);
        }
      }
    } catch (error) {
      console.error('Erreur IA:', error);
      toast.error('Erreur de communication avec l\'IA');
    }
  };

  // Synthèse vocale (TTS)
  const speakText = async (text) => {
    setIsSpeaking(true);
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/integrations/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify({
          text: text.substring(0, 4000), // Limite TTS
          voice: selectedVoice
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.audio_base64) {
          // Convertir base64 en audio
          const audioData = atob(data.audio_base64);
          const audioArray = new Uint8Array(audioData.length);
          for (let i = 0; i < audioData.length; i++) {
            audioArray[i] = audioData.charCodeAt(i);
          }
          const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
          const audioUrl = URL.createObjectURL(audioBlob);
          
          const audio = new Audio(audioUrl);
          audio.onended = () => {
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
          };
          audio.onerror = () => {
            setIsSpeaking(false);
          };
          await audio.play();
        }
      }
    } catch (error) {
      console.error('Erreur TTS:', error);
      setIsSpeaking(false);
    }
  };

  // Sauvegarder la transcription
  const saveTranscript = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const docData = {
        titre: reunionTitle || `${mode === 'reunion' ? 'Réunion' : 'Conversation'} du ${new Date().toLocaleDateString('fr-FR')}`,
        type: mode === 'reunion' ? 'compte_rendu_reunion' : 'conversation_ia',
        contenu: transcript.map(t => `[${new Date(t.timestamp).toLocaleTimeString('fr-FR')}] ${t.role === 'user' ? 'Vous' : 'IA'}: ${t.content}`).join('\n\n'),
        metadata: {
          duree_secondes: reunionDuration,
          nb_messages: transcript.length,
          agent: selectedAgent,
          mode: mode
        }
      };
      
      const response = await fetch(`${API_URL}/api/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify(docData)
      });
      
      if (response.ok) {
        toast.success('📄 Transcription sauvegardée !');
      } else {
        toast.error('Erreur de sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur de sauvegarde');
    }
  };

  // Télécharger en texte
  const downloadTranscript = () => {
    const text = transcript.map(t => 
      `[${new Date(t.timestamp).toLocaleTimeString('fr-FR')}] ${t.role === 'user' ? 'Vous' : 'IA'}: ${t.content}`
    ).join('\n\n');
    
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcription_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Réinitialiser
  const resetConversation = () => {
    setTranscript([]);
    setReunionDuration(0);
    setReunionTitle('');
    setCurrentText('');
  };

  const selectedAgentData = agents.find(a => a.id === selectedAgent);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assistant-vocal-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Assistant Vocal</h1>
          <p className="text-muted-foreground mt-1">
            Parlez avec vos agents IA ou enregistrez vos réunions
          </p>
        </div>
        
        {/* Sélecteur de mode */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-muted rounded-lg p-1">
            <Button
              variant={mode === 'conversation' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMode('conversation')}
              className="gap-2"
            >
              <Bot className="w-4 h-4" />
              Conversation IA
            </Button>
            <Button
              variant={mode === 'reunion' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setMode('reunion')}
              className="gap-2"
            >
              <Phone className="w-4 h-4" />
              Mode Réunion
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Panneau de contrôle */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Contrôles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Sélection agent (mode conversation) */}
            {mode === 'conversation' && (
              <div className="space-y-2">
                <Label>Agent IA</Label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map(agent => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Sélection voix */}
            <div className="space-y-2">
              <Label>Voix de l'assistant</Label>
              <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nova">Nova (Féminine)</SelectItem>
                  <SelectItem value="alloy">Alloy (Neutre)</SelectItem>
                  <SelectItem value="echo">Echo (Masculine)</SelectItem>
                  <SelectItem value="fable">Fable (Expressive)</SelectItem>
                  <SelectItem value="onyx">Onyx (Grave)</SelectItem>
                  <SelectItem value="shimmer">Shimmer (Douce)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Options */}
            {mode === 'conversation' && (
              <div className="flex items-center justify-between">
                <Label htmlFor="auto-speak">Réponse vocale auto</Label>
                <Switch
                  id="auto-speak"
                  checked={autoSpeak}
                  onCheckedChange={setAutoSpeak}
                />
              </div>
            )}

            {/* Titre réunion (mode réunion) */}
            {mode === 'reunion' && (
              <div className="space-y-2">
                <Label>Titre de la réunion</Label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Ex: Réunion équipe du 12/02"
                  value={reunionTitle}
                  onChange={(e) => setReunionTitle(e.target.value)}
                />
              </div>
            )}

            {/* Durée (mode réunion) */}
            {mode === 'reunion' && isListening && (
              <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-red-600">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="font-mono text-2xl font-bold">
                    {formatDuration(reunionDuration)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Enregistrement en cours</p>
              </div>
            )}

            {/* Bouton principal */}
            <div className="pt-4 space-y-3">
              {!isListening ? (
                <Button 
                  className="w-full h-16 text-lg gap-3"
                  onClick={startListening}
                >
                  <Mic className="w-6 h-6" />
                  {mode === 'conversation' ? 'Démarrer la conversation' : 'Démarrer l\'enregistrement'}
                </Button>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 h-12"
                      onClick={togglePause}
                    >
                      {isPaused ? <Play className="w-5 h-5 mr-2" /> : <Pause className="w-5 h-5 mr-2" />}
                      {isPaused ? 'Reprendre' : 'Pause'}
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1 h-12"
                      onClick={stopListening}
                    >
                      <StopCircle className="w-5 h-5 mr-2" />
                      Arrêter
                    </Button>
                  </div>
                </div>
              )}

              {/* Indicateur d'état */}
              {isListening && !isPaused && (
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Mic className="w-5 h-5 animate-pulse" />
                  <span className="text-sm font-medium">
                    {currentText || 'À l\'écoute...'}
                  </span>
                </div>
              )}

              {isSpeaking && (
                <div className="flex items-center justify-center gap-2 text-blue-600">
                  <Volume2 className="w-5 h-5 animate-pulse" />
                  <span className="text-sm font-medium">L'assistant parle...</span>
                </div>
              )}
            </div>

            {/* Actions sur transcript */}
            {transcript.length > 0 && (
              <div className="pt-4 border-t space-y-2">
                <Button variant="outline" className="w-full gap-2" onClick={saveTranscript}>
                  <Save className="w-4 h-4" />
                  Sauvegarder
                </Button>
                <Button variant="outline" className="w-full gap-2" onClick={downloadTranscript}>
                  <Download className="w-4 h-4" />
                  Télécharger (.txt)
                </Button>
                <Button variant="ghost" className="w-full text-destructive" onClick={resetConversation}>
                  Effacer la conversation
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zone de transcription */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {mode === 'conversation' ? 'Conversation' : 'Transcription de réunion'}
              </span>
              {transcript.length > 0 && (
                <Badge variant="secondary">
                  {transcript.length} message{transcript.length > 1 ? 's' : ''}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px] pr-4">
              {transcript.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
                  <Mic className="w-16 h-16 mb-4 opacity-20" />
                  <p className="text-lg font-medium">
                    {mode === 'conversation' 
                      ? 'Cliquez sur "Démarrer" et parlez à votre assistant'
                      : 'Cliquez sur "Démarrer" pour enregistrer votre réunion'}
                  </p>
                  <p className="text-sm mt-2">
                    {mode === 'conversation'
                      ? 'L\'IA vous répondra à voix haute'
                      : 'Tout sera transcrit automatiquement'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {transcript.map((entry, index) => (
                    <div
                      key={index}
                      className={`flex ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                          entry.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {entry.role === 'user' ? (
                            <User className="w-4 h-4" />
                          ) : (
                            <Bot className="w-4 h-4" />
                          )}
                          <span className="text-xs opacity-70">
                            {new Date(entry.timestamp).toLocaleTimeString('fr-FR')}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{entry.content}</p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Texte en cours */}
                  {currentText && (
                    <div className="flex justify-end">
                      <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-primary/50 text-primary-foreground">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">{currentText}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Bot className="w-5 h-5" />
                Mode Conversation IA
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Parlez naturellement à votre assistant</li>
                <li>• L'IA transcrit, comprend et répond à voix haute</li>
                <li>• Tout est enregistré à l'écrit en temps réel</li>
                <li>• Idéal pour des questions rapides ou du brainstorming</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Mode Réunion
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Branchez pendant vos réunions, visio ou appels</li>
                <li>• Transcription continue de tout ce qui est dit</li>
                <li>• Sauvegardez comme compte-rendu de réunion</li>
                <li>• Téléchargez en texte pour archivage</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssistantVocalPage;
